import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Alert,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import ViewShot from 'react-native-view-shot';
import { useAuth } from '../context/AuthContext';
import { useStudentProfilePhoto } from '../hooks/useStudentProfilePhoto';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Toast } from '../components/common/Toast';
import { WebPageShell } from '../components/common/WebPageShell';
import { Barcode } from '../components/common/Barcode';
import { useTheme, ThemeColors } from '../context/ThemeContext';

let CameraRoll: any;
let RNHTMLtoPDF: any;
if (Platform.OS !== 'web') {
  CameraRoll = require('@react-native-camera-roll/camera-roll').CameraRoll;
  RNHTMLtoPDF = require('react-native-html-to-pdf');
}

type NavigationProp = NativeStackNavigationProp<any>;

export function StudentIdScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { selectedStudent, user } = useAuth();
  const { displayPhoto } = useStudentProfilePhoto(selectedStudent?.id || '', selectedStudent || undefined);
  const { colors, isDark } = useTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const viewShotRef = React.useRef<React.ElementRef<typeof ViewShot>>(null);
  const captureNodeRef = React.useRef<any>(null);

  const [showToast, setShowToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState('');

  const showToastMsg = (msg: string) => {
    setToastMessage(msg);
    setShowToast(true);
  };

  const handleDownload = async () => {
    try {
      const filename = `Student_ID_${selectedStudent?.name.replace(/\s+/g, '_')}`;

      if (Platform.OS === 'web') {
        // On web the card is rendered straight from the DOM rather than from a
        // captured bitmap: the text and barcode stay sharp at any zoom.
        const node = captureNodeRef.current as any;
        if (!node) {
          return;
        }

        // The PDF page is sized to the CONTENT, not fixed at A4. The card plus the
        // contacts section together are taller than one A4 page fits with reasonable
        // margins, so a fixed A4 page forced a second, mostly-blank page for the
        // overflow no matter how the margins were tuned - the export needs the ID
        // card AND the contacts section on one page, and the only way to guarantee
        // that regardless of exactly how tall the content renders is to make the
        // page exactly as tall as the content. A custom jsPDF format does that: with
        // no page taller than the content, there is nothing left over to overflow.
        const MARGIN_MM = 12;
        const contentWidthMm = 180;
        const contentHeightMm = contentWidthMm * (node.offsetHeight / node.offsetWidth);
        const pageWidthMm = contentWidthMm + MARGIN_MM * 2;
        const pageHeightMm = contentHeightMm + MARGIN_MM * 2;

        const html2pdf = require('html2pdf.js');
        const opt = {
          margin: MARGIN_MM,
          filename: `${filename}.pdf`,
          // PNG rather than JPEG: the barcode's thin bars must not pick up
          // compression artefacts a scanner would trip over.
          image: { type: 'png', quality: 1 },
          html2canvas: { scale: 3, useCORS: true, backgroundColor: '#FFFFFF' },
          jsPDF: { unit: 'mm', format: [pageWidthMm, pageHeightMm], orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        };

        await html2pdf().set(opt).from(node).save();
        showToastMsg('ID card (PDF) downloaded');
        return;
      }

      if (viewShotRef.current?.capture) {
        const uri = await viewShotRef.current.capture();

        const htmlString = `
          <html>
            <head>
              <meta name="viewport" content="width=device-width, initial-scale=1" />
              <style>
                @page { margin: 0; }
                html, body { margin: 0; padding: 0; height: 100%; }
                .page {
                  min-height: 100vh;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 24px;
                  box-sizing: border-box;
                }
                img { max-width: 100%; height: auto; }
              </style>
            </head>
            <body>
              <div class="page"><img src="${uri}" /></div>
            </body>
          </html>
        `;

        {
          try {
            const filename = `Student_ID_${selectedStudent?.name.replace(/\s+/g, '_')}`;
            const options = {
              html: htmlString,
              fileName: filename,
              directory: 'Documents',
            };
            const file = await RNHTMLtoPDF.convert(options);
            showToastMsg('ID card (PDF) downloaded');
          } catch (saveError) {
            console.error('Error saving PDF:', saveError);
            Alert.alert('Error', 'Failed to generate PDF.');
          }
        }
      }
    } catch (error: any) {
      if (error.message !== 'User did not share') {
        console.error('Error capturing ID card:', error);
        Alert.alert('Error', 'Failed to capture ID card.');
      }
    }
  };

  if (!selectedStudent) {
    return (
      <WebPageShell>
        <View style={styles.container}>
          <Text>No student selected</Text>
        </View>
      </WebPageShell>
    );
  }

  const raw = selectedStudent.rawSnapshot || {};
  const profile = raw.Profile || raw.profile || {};
  const enrollments = raw.Enrollments || raw.enrollments || [];
  const relatives = raw.Relatives || raw.relatives || [];
  const activeEnrollment = enrollments.find((e: any) => e.IsActive ?? e.isActive) || enrollments[0] || {};

  const emergencyContact = relatives.find((r: any) => r.IsEmergencyContact || r.isEmergencyContact) || relatives[0];
  const emergencyName = emergencyContact ? (`${emergencyContact.FirstName || emergencyContact.firstName || ''} ${emergencyContact.LastName || emergencyContact.lastName || ''}`.trim() || emergencyContact.RelationName || emergencyContact.relationName || 'N/A') : 'N/A';
  const emergencyPhone = emergencyContact ? (emergencyContact.Phone || emergencyContact.phone || emergencyContact.CellPhone || emergencyContact.cellPhone || 'N/A') : 'N/A';

  // Unlike emergency contact above, this does not fall back to relatives[0] — showing an
  // arbitrary relative as the "financial contact" when none is actually designated would be wrong.
  const financialContact = relatives.find((r: any) => r.IsBillingContact || r.isBillingContact);
  const financialName = financialContact ? (`${financialContact.FirstName || financialContact.firstName || ''} ${financialContact.LastName || financialContact.lastName || ''}`.trim() || financialContact.RelationName || financialContact.relationName || 'N/A') : 'N/A';
  const financialPhone = financialContact ? (financialContact.Phone || financialContact.phone || financialContact.CellPhone || financialContact.cellPhone || 'N/A') : 'N/A';

  const address = [
    profile.Address1 || profile.address1 || (selectedStudent as any).address1,
    profile.Address2 || profile.address2 || (selectedStudent as any).address2,
    profile.POBox || profile.poBox,
    profile.District || profile.district,
    profile.State || profile.state,
    profile.Country || profile.country,
    profile.Pincode || profile.pincode,
  ]
    .filter(Boolean)
    .join(', ') || 'N/A';

  // Enrollment.AcademicYear stores the START year of the range: an enrollment
  // active today (Aug 2026) reads 2026, meaning the 2026-2027 school year, not
  // the 2025-2026 year that already ended months ago. (The previous "END year"
  // assumption here had this backwards.)
  const academicYearStart = activeEnrollment.AcademicYear ?? activeEnrollment.academicYear ?? selectedStudent.academicYear;
  const academicYearStr = academicYearStart ? `${academicYearStart}-${academicYearStart + 1}` : '';

  // Prefers the actual enrollment end date over academicYearStr: the card now shows
  // the academic year in its own field above, so falling back to that same string
  // here would print "2026-27" twice for no reason.
  const validThroughDate = activeEnrollment.EndDate || activeEnrollment.endDate;
  const validThroughStr = validThroughDate
    ? new Date(validThroughDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    : academicYearStr || 'N/A';

  const studentIdNumber = selectedStudent.studentIdentificationNumber || '';

  return (
    <WebPageShell title="Student ID Card" maxWidth={800}>
      <View style={styles.screenHeader}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.navigate('Profile' as any)}>
          <FeatherIcon name="chevron-left" size={20} color={colors.textPrimary} />
        </TouchableOpacity>
        <View style={{ flex: 1 }} />
      </View>

      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ViewShot ref={viewShotRef} options={{ format: 'png', quality: 1.0, result: 'data-uri' }} style={styles.captureArea}>
          <View ref={captureNodeRef} style={styles.captureArea}>
            <View style={styles.idCardContainer}>
              <View style={styles.orangeTopEdge} />

              <View style={[styles.blueSection, isMobile && styles.blueSectionMobile]}>
                <View style={[styles.cardHeader, isMobile && styles.cardHeaderMobile]}>
                  <View style={styles.schoolLogoRow}>
                    <View style={styles.schoolLogo}>
                      <Image source={require('../assets/images/cayman_icon.png')} style={styles.schoolLogoImage} resizeMode="contain" />
                    </View>
                    <View style={styles.schoolText}>
                      <Text style={styles.schoolName}>CAYMAN ACADEMY</Text>
                      <Text style={styles.schoolSubName}>A SEVENTH-DAY ADVENTIST INSTITUTION</Text>
                    </View>
                  </View>
                  <View style={styles.studentIdBadge}>
                    <Text style={styles.studentIdBadgeText}>STUDENT ID</Text>
                  </View>
                </View>

                <View style={[styles.studentInfoSection, isMobile && styles.studentInfoSectionMobile]}>
                  <View style={[styles.photoContainer, isMobile && styles.photoContainerMobile]}>
                    {displayPhoto ? (
                      <Image source={{ uri: displayPhoto }} style={styles.photo} resizeMode="cover" />
                    ) : (
                      <Text style={styles.photoPlaceholder}>{selectedStudent.name.charAt(0).toUpperCase()}</Text>
                    )}
                  </View>

                  <View style={[styles.studentDetails, isMobile && styles.studentDetailsMobile]}>
                    <Text style={[styles.studentName, isMobile && styles.studentNameMobile]}>{selectedStudent.name}</Text>

                    {/* Two columns: Grade and Academic Year on the left; Student ID with the Address under it on the right. */}
                    <View style={[styles.detailsColumns, isMobile && styles.detailsColumnsMobile]}>
                      <View style={styles.detailsColumn}>
                        <View style={styles.detailBoxFull}>
                          <Text style={styles.detailLabel}>Grade</Text>
                          <Text style={styles.detailValue}> {selectedStudent.class} </Text>
                        </View>
                        {!!academicYearStr && (
                          <View style={styles.detailBoxFull}>
                            <Text style={styles.detailLabel}>ACADEMIC YEAR</Text>
                            <Text style={styles.detailValue}>{academicYearStr}</Text>
                          </View>
                        )}
                      </View>
                      <View style={styles.detailsColumn}>
                        <View style={styles.detailBoxFull}>
                          <Text style={styles.detailLabel}>STUDENT ID</Text>
                          <Text style={styles.detailValue}>{selectedStudent.studentIdentificationNumber}</Text>
                        </View>
                        <View style={styles.detailBoxFull}>
                          <Text style={styles.detailLabel}>ADDRESS</Text>
                          <Text style={styles.detailValue}>{address}</Text>
                        </View>
                      </View>
                    </View>
                  </View>
                </View>
              </View>

              <View style={[styles.whiteSection, isMobile && styles.whiteSectionMobile]}>
                <View style={styles.barcodeWrapper}>
                  <Barcode value={studentIdNumber} height={40} color={colors.textPrimary} />
                </View>
                <View style={[styles.validThrough, isMobile && styles.validThroughMobile]}>
                  <Text style={styles.validText}>Valid through</Text>
                  <Text style={styles.validDate}>{validThroughStr}</Text>
                </View>
              </View>
            </View>

            <View style={styles.infoCard}>
              <View style={styles.infoRow}>
                <View style={[styles.iconBox, { backgroundColor: '#FFEDD5' }]}>
                  <FeatherIcon name="phone" size={16} color="#EA580C" />
                </View>
                <View style={styles.infoTextContainer}>
                  <Text style={styles.infoLabel}>Emergency contact</Text>
                  <Text style={styles.infoValue}>{emergencyName} • {emergencyPhone}</Text>
                </View>
              </View>
              <View style={styles.divider} />

              {financialContact && (
                <>
                  <View style={styles.infoRow}>
                    <View style={[styles.iconBox, { backgroundColor: '#DCFCE7' }]}>
                      <FeatherIcon name="credit-card" size={16} color="#16A34A" />
                    </View>
                    <View style={styles.infoTextContainer}>
                      <Text style={styles.infoLabel}>Financial contact</Text>
                      <Text style={styles.infoValue}>{financialName} • {financialPhone}</Text>
                    </View>
                  </View>
                  <View style={styles.divider} />
                </>
              )}

              <View style={styles.infoRow}>
                <View style={[styles.iconBox, { backgroundColor: colors.primaryLight }]}>
                  <FeatherIcon name="map-pin" size={16} color={colors.primary} />
                </View>
                <View style={styles.infoTextContainer}>
                  <Text style={styles.infoLabel}>Home address</Text>
                  <Text style={styles.infoValue}>{address}</Text>
                </View>
              </View>
              {/* Transport row hidden: it cannot be edited from here.
              <View style={styles.divider} />

              <View style={styles.infoRow}>
                <View style={[styles.iconBox, { backgroundColor: '#FFEDD5' }]}>
                  <FeatherIcon name="truck" size={16} color="#EA580C" />
                </View>
                <View style={styles.infoTextContainer}>
                  <Text style={styles.infoLabel}>Transport</Text>
                  <Text style={styles.infoValue}>{profile.TransportRoute || profile.transportRoute || 'N/A'}</Text>
                </View>
              </View>
              */}
            </View>
          </View>
        </ViewShot>

        <View style={[styles.bottomActions, { paddingHorizontal: 0 }]}>
          <TouchableOpacity style={styles.downloadBtn} onPress={handleDownload}>
            <FeatherIcon name="download" size={18} color="#FFFFFF" />
            <Text style={styles.downloadBtnText}>Download</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      <Toast
        visible={showToast}
        message={toastMessage}
        type="success"
        onDismiss={() => setShowToast(false)}
      />
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors, isDark: boolean) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: colors.background,
  },
  screenHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  screenTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: colors.textPrimary,
  },
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 40,
  },
  captureArea: {
    backgroundColor: colors.background,
  },
  idCardContainer: {
    borderRadius: 20,
    backgroundColor: colors.card,
    // Preserved as the original literal navy in light mode, matching the ID card's
    // brand section below — must not shift with the shared "primary" theme token.
    shadowColor: isDark ? colors.primary : '#1E40AF',
    shadowOpacity: 0.15,
    shadowRadius: 15,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
    marginBottom: 24,
    overflow: 'hidden',
  },
  orangeTopEdge: {
    height: 6,
    backgroundColor: '#F97316',
    width: '100%',
  },
  blueSection: {
    backgroundColor: isDark ? colors.primary : '#1E40AF',
    padding: 20,
  },
  blueSectionMobile: {
    padding: 14,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  cardHeaderMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 16,
  },
  schoolLogoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
  },
  schoolText: {
    flexShrink: 1,
  },
  schoolLogo: {
    // Square, not the previous 34x40: a crest badge is closer to 1:1, and the
    // uneven box let "contain" letterbox the image off-centre inside it.
    width: 42,
    height: 42,
    justifyContent: 'center',
    alignItems: 'center',
  },
  schoolLogoImage: {
    width: '100%',
    height: '100%',
  },
  schoolName: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 15,
    lineHeight: 18,
    letterSpacing: 0.8,
  },
  schoolSubName: {
    color: '#FBBF24',
    fontSize: 8,
    fontWeight: '700',
    letterSpacing: 0.6,
  },
  studentIdBadge: {
    flexShrink: 0,
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  studentIdBadgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '700',
  },
  studentInfoSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  studentInfoSectionMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  photoContainer: {
    width: 90,
    height: 90,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    marginRight: 16,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: '#FFFFFF',
    overflow: 'hidden',
  },
  photoContainerMobile: {
    width: 70,
    height: 70,
    marginRight: 0,
    marginBottom: 14,
  },
  photo: {
    width: '100%',
    height: '100%',
  },
  photoPlaceholder: {
    fontSize: 32,
    fontWeight: '700',
    color: colors.primary,
  },
  studentDetails: {
    flex: 1,
  },
  studentDetailsMobile: {
    width: '100%',
    flex: undefined,
  },
  studentName: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '800',
    marginBottom: 12,
  },
  studentNameMobile: {
    fontSize: 17,
  },
  detailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  detailsColumns: {
    flexDirection: 'row',
    gap: 24,
  },
  detailsColumnsMobile: {
    flexDirection: 'column',
    gap: 12,
  },
  detailsColumn: {
    flex: 1,
    gap: 12,
  },
  detailBox: {
    width: '45%',
  },
  detailBoxMobile: {
    width: '100%',
  },
  detailBoxFull: {
    width: '100%',
  },
  detailLabel: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 9,
    fontWeight: '700',
    marginBottom: 2,
  },
  detailValue: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '700',
  },
  whiteSection: {
    backgroundColor: colors.card,
    flexDirection: 'row',
    padding: 20,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  whiteSectionMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 14,
    gap: 12,
  },
  barcodeWrapper: {
    flex: 1,
    marginRight: 16,
  },
  validThrough: {
    alignItems: 'flex-end',
  },
  validThroughMobile: {
    alignItems: 'flex-start',
  },
  validText: {
    fontSize: 10,
    color: colors.textMuted,
    fontWeight: '600',
  },
  validDate: {
    fontSize: 13,
    color: colors.textPrimary,
    fontWeight: '800',
  },
  infoCard: {
    backgroundColor: colors.card,
    borderRadius: 20,
    padding: 16,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  iconBox: {
    width: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  infoTextContainer: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 11,
    color: colors.textMuted,
    fontWeight: '600',
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 13,
    color: colors.textPrimary,
    fontWeight: '700',
  },
  divider: {
    height: 1,
    backgroundColor: colors.divider,
    marginLeft: 48,
  },
  bottomActions: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 20,
    backgroundColor: colors.background,
    gap: 12,
  },
  downloadBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1E293B',
    borderRadius: 12,
    height: 48,
    gap: 8,
  },
  downloadBtnText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '700',
  },
});
